import { ResourcesValue } from '../../resources/resources';
import { type LifecycleHooks } from '../addLifecycleHooks';

export interface ChecklistFormValues {
  id?: number;
  title: string;
  frequency: 'RECURRING' | 'ONE_OFF';
  startOn: string;
  dueAfterAmount: number;
  dueAfterUnit: 'MINUTES' | 'HOURS' | 'DAYS';
  repeatEveryAmount: number;
  repeatEveryUnit: 'DAYS' | 'WEEKS';
  repeatEveryWeekDays?: (
    | 'SUNDAY'
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
  )[];
  recurrenceEnds: 'NEVER' | 'AFTER_OCCURRENCES' | 'ON_DATE';
  recurrenceEndsOnDate?: string;
  recurrenceEndsAfterOccurrences?: number;
  notifyUsersWhenChecklistStarted: boolean;
  notifyUsersWhenChecklistOverdue: boolean;
  tasks: ChecklistTaskFormValues[];
  userGroupIds: number[];
}

export interface ChecklistTaskFormValues {
  id?: number;
  type: 'TASK' | 'OPEN_ENDED' | 'RADIO_SELECT' | 'PHOTO_UPLOAD';
  title: string;
  description?: string | null;
  /** For all except `TASK` */
  required?: boolean;
  /** For `RADIO_SELECT` */
  options?: { text: string | null }[];
}

export interface ChecklistFormApiModel {
  id?: number;
  title: string;
  startOn: string;
  dueAfterSeconds: number;
  /** Only set if not a ONE_OFF checklist */
  recurrenceScheduleConfig?: {
    version: 1;
    schedule: {
      repeatEvery: {
        amount: number;
        unit: 'DAYS' | 'WEEKS';
        daysOfWeek?: (
          | 'SUNDAY'
          | 'MONDAY'
          | 'TUESDAY'
          | 'WEDNESDAY'
          | 'THURSDAY'
          | 'FRIDAY'
          | 'SATURDAY'
        )[];
      };
      endCondition: {
        type: 'NEVER_ENDS' | 'ENDS_ON_DATE' | 'ENDS_AFTER_OCCURRENCES';
        /** Required if type is `ENDS_ON_DATE` */
        endOn?: string;
        /** Required if type is `ENDS_AFTER_OCCURRENCES` */
        occurrences?: number;
      };
    };
  };
  notifyUsersWhenChecklistStarted: boolean;
  notifyUsersWhenChecklistOverdue: boolean;
  tasks: {
    id?: number;
    title: string;
    description?: string;
    displayOrder: number;
    fieldConfig: {
      version: 1;
      config: {
        version: 1;
        type: 'TASK' | 'OPEN_ENDED' | 'RADIO_SELECT' | 'PHOTO_UPLOAD';
        /** For all types except `TASK` */
        required?: boolean;
        /** Required if `type = RADIO_SELECT` */
        options?: (string | null)[];
      };
    };
  }[];
  assigneeGroupIds: number[];
}

export const checklistsDataTransform: LifecycleHooks[] = [
  {
    resource: ResourcesValue.CHECKLISTS,
    afterGetOne: (res) => {
      const checklist: ChecklistFormApiModel = res.data;

      const dueAfterUnit =
        checklist.dueAfterSeconds % (60 * 60 * 24) === 0
          ? 'DAYS'
          : checklist.dueAfterSeconds % (60 * 60) === 0
            ? 'HOURS'
            : 'MINUTES';

      const form = {
        id: checklist.id,
        title: checklist.title,
        startOn: checklist.startOn,
        dueAfterUnit,
        dueAfterAmount:
          dueAfterUnit === 'DAYS'
            ? checklist.dueAfterSeconds / (60 * 60 * 24)
            : dueAfterUnit === 'HOURS'
              ? checklist.dueAfterSeconds / (60 * 60)
              : checklist.dueAfterSeconds / 60,
        frequency: checklist.recurrenceScheduleConfig ? 'RECURRING' : 'ONE_OFF',
        repeatEveryAmount:
          checklist.recurrenceScheduleConfig?.schedule.repeatEvery.amount ?? 1,
        repeatEveryUnit:
          checklist.recurrenceScheduleConfig?.schedule.repeatEvery.unit ??
          'DAYS',
        repeatEveryWeekDays:
          checklist.recurrenceScheduleConfig?.schedule.repeatEvery.daysOfWeek ??
          [],
        recurrenceEnds: checklist.recurrenceScheduleConfig
          ? checklist.recurrenceScheduleConfig.schedule.endCondition.type ===
            'NEVER_ENDS'
            ? 'NEVER'
            : checklist.recurrenceScheduleConfig.schedule.endCondition.type ===
                'ENDS_ON_DATE'
              ? 'ON_DATE'
              : 'AFTER_OCCURRENCES'
          : 'NEVER',
        recurrenceEndsOnDate:
          checklist.recurrenceScheduleConfig?.schedule.endCondition.endOn,
        recurrenceEndsAfterOccurrences:
          checklist.recurrenceScheduleConfig?.schedule.endCondition.occurrences,
        notifyUsersWhenChecklistStarted:
          checklist.notifyUsersWhenChecklistStarted,
        notifyUsersWhenChecklistOverdue:
          checklist.notifyUsersWhenChecklistOverdue,
        tasks: [...checklist.tasks]
          .sort((taskA, taskB) => taskA.displayOrder - taskB.displayOrder)
          .map<ChecklistTaskFormValues>((task) => ({
            id: task.id,
            title: task.title,
            description: task.description || undefined,
            type: task.fieldConfig.config.type,
            required: task.fieldConfig.config.required,
            options: task.fieldConfig.config.options?.map((option) => ({
              text: option,
            })),
          })),
        userGroupIds: checklist.assigneeGroupIds,
      } satisfies ChecklistFormValues;

      return Promise.resolve({
        ...res,
        data: form,
      });
    },
    beforeSave: (data: ChecklistFormValues) => {
      // Convert the form values to the shape expected by the API
      const checklist = {
        id: data.id,
        title: data.title,
        startOn: data.startOn,
        dueAfterSeconds:
          data.dueAfterUnit === 'MINUTES'
            ? data.dueAfterAmount * 60
            : data.dueAfterUnit === 'HOURS'
              ? data.dueAfterAmount * 60 * 60
              : data.dueAfterAmount * 60 * 60 * 24,
        recurrenceScheduleConfig:
          data.frequency === 'RECURRING'
            ? {
                version: 1,
                schedule: {
                  repeatEvery: {
                    amount: data.repeatEveryAmount,
                    unit: data.repeatEveryUnit === 'DAYS' ? 'DAYS' : 'WEEKS',
                    daysOfWeek:
                      data.repeatEveryUnit === 'WEEKS'
                        ? data.repeatEveryWeekDays
                        : undefined,
                  },
                  endCondition: {
                    type:
                      data.recurrenceEnds === 'NEVER'
                        ? 'NEVER_ENDS'
                        : data.recurrenceEnds === 'ON_DATE'
                          ? 'ENDS_ON_DATE'
                          : 'ENDS_AFTER_OCCURRENCES',
                    endOn:
                      data.recurrenceEnds === 'ON_DATE'
                        ? data.recurrenceEndsOnDate
                        : undefined,
                    occurrences:
                      data.recurrenceEnds === 'AFTER_OCCURRENCES'
                        ? data.recurrenceEndsAfterOccurrences
                        : undefined,
                  },
                },
              }
            : undefined,
        notifyUsersWhenChecklistStarted: data.notifyUsersWhenChecklistStarted,
        notifyUsersWhenChecklistOverdue: data.notifyUsersWhenChecklistOverdue,
        tasks: data.tasks.map<ChecklistFormApiModel['tasks'][number]>(
          (task, index) => ({
            id: task.id,
            title: task.title,
            description: task.description || undefined,
            displayOrder: index,
            fieldConfig: {
              version: 1,
              config: {
                version: 1,
                type: task.type,
                required: task.required,
                options: task.options?.map((option) => option.text),
              },
            },
          })
        ),
        assigneeGroupIds: data.userGroupIds,
      } satisfies ChecklistFormApiModel;

      return Promise.resolve(checklist);
    },
  },
];
